/**
 * Integrytas Group - Common UI
 *
 * Opens a child record from the related-children drill-down.
 *
 * Resolution order:
 *   1. A registered child editor for the group's `entity` — the child's OWN
 *      edit form, so the drill-down is the real editor rather than a second
 *      rendering that drifts from it. Editable when the caller holds
 *      can_change on the CHILD's page; otherwise the same form, read-only.
 *   2. Otherwise a generic read-only field view fetched from the group's
 *      `table`/`pkColumn`, so a child whose editor is not registered yet is
 *      still openable instead of being a dead click.
 *   3. If the group declares neither, the panel does not make the row
 *      clickable at all and this component is never rendered.
 */
import { useEffect, useState } from 'react';
import { apiDb } from '../../lib/api_client';
import { hasAccess, type AuthSession } from '../../types/auth';
import {
  getChildEditor,
  ChildEditorReadOnlyProvider,
} from '../../lib/child_editors';
import { Modal } from './Modal';
import { YesNoBadge } from './YesNoBadge';

export interface ChildDetailTarget {
  entity?: string;
  table?: string;
  pkColumn?: string;
  pk: string | number;
  label: string;
}

/** Columns a generic view must never show: surrogate keys and audit plumbing (§17.B4). */
const HIDDEN = /^(.*_id|id|created_at|updated_at|modified_by|row_version|ext_attributes)$/i;

function prettify(column: string): string {
  return column.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function GenericChildView({ table, pkColumn, pk }: { table: string; pkColumn: string; pk: string | number }) {
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setBusy(true);
      const { data, error: e } = await apiDb.from(table).select('*').eq(pkColumn, pk).maybeSingle();
      if (cancelled) return;
      if (e) setError(e.message); else setRow((data as Record<string, unknown>) ?? null);
      setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [table, pkColumn, pk]);

  if (busy) return <div className="px-1 py-3 text-csm-sm text-csm-muted">Loading…</div>;
  if (error) return <div className="px-1 py-3 text-csm-sm text-csm-danger">{error}</div>;
  if (!row) return <div className="px-1 py-3 text-csm-sm text-csm-muted">Record not found.</div>;

  const fields = Object.entries(row).filter(([k, v]) => !HIDDEN.test(k) && v !== null && v !== '');

  return (
    <dl className="grid grid-cols-[minmax(9rem,auto)_1fr] gap-x-4 gap-y-1.5 px-1 py-2 text-csm-sm">
      {fields.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{prettify(k)}</dt>
          <dd className="truncate text-csm-ink" title={String(v)}>
            {/* §17.B10: a Y/N char(1) never reaches the user as a bare letter. */}
            {v === 'Y' || v === 'N'
              ? <YesNoBadge value={v as 'Y' | 'N'} />
              : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ChildDetailModal({
  session, target, onClose, onSaved,
}: {
  session: AuthSession;
  target: ChildDetailTarget;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const entry = getChildEditor(target.table);

  if (entry) {
    // The verb belongs to the CHILD's page: opening a Role from the Users page
    // asks for can_change on the ROLE page, not on Users.
    const readOnly = !hasAccess(session, entry.pageCode, 'change');
    const { Editor } = entry;
    return (
      // The context is the whole mechanism: Modal reads it and makes its BODY
      // inert while leaving ×/Cancel alive. Wrapping the editor in a disabled
      // fieldset out here also caught the modal chrome and trapped the user.
      <ChildEditorReadOnlyProvider value={readOnly}>
        <Editor
          session={session}
          pk={target.pk}
          readOnly={readOnly}
          onClose={onClose}
          onSaved={() => { onSaved?.(); onClose(); }}
        />
      </ChildEditorReadOnlyProvider>
    );
  }

  // No editor registered yet — a read-only view beats a dead click.
  return (
    <Modal title={target.label} onClose={onClose} width="lg">
      {target.table && target.pkColumn
        ? <GenericChildView table={target.table} pkColumn={target.pkColumn} pk={target.pk} />
        : <div className="px-1 py-3 text-csm-sm text-csm-muted">No detail view available for this record.</div>}
    </Modal>
  );
}
